<script setup lang="ts">
import {computed, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";

// Emits a generic patch event, handled by parent (itemV2Main.vue)
const emit = defineEmits(["patch"]);

const {t} = useI18n();
const store = useStore();
const route = useRoute();
const pageStr = "components.itemV2.itemV2Name"

// Access the session from the store (keeps consistency with the rest of the app)
const session = ref(store.getters["session/get"]);

// Current item index from the route (fallback to 0)
const itemIdx = computed(() => {
  const raw = route.params.itemIdx;
  if (typeof raw === "string") {
    const n = parseInt(raw, 10);
    return isNaN(n) ? 0 : n;
  }
  if (typeof raw === "number") return raw;
  return 0;
});

// Determine selected person
// Prefer a person already associated with the current item; otherwise default to the first person
const selectedPersonIdx = ref(0);

const persons = computed(() => session.value?.persons ?? []);

onMounted(() => {
  if (persons.value.length === 0) return;
  const idx = persons.value.findIndex((p: any) => Array.isArray(p.items) && p.items.includes(itemIdx.value));
  selectedPersonIdx.value = idx >= 0 ? idx : 0;
  ensureItemIncluded();
});

watch(selectedPersonIdx, () => {
  ensureItemIncluded();
});

// The editable name bound to the input
const nameModel = ref("");

// Keep nameModel in sync with selected person
watch(
    () => [selectedPersonIdx.value, persons.value?.[selectedPersonIdx.value]?.name],
    () => {
      const p = persons.value?.[selectedPersonIdx.value];
      nameModel.value = p?.name ?? "";
    },
    {immediate: true}
);

// Ensure the current item is listed for the selected person (frontend side)
const ensureItemIncluded = () => {
  const p = persons.value?.[selectedPersonIdx.value];
  if (!p) return;
  if (!Array.isArray(p.items)) p.items = [];
  if (!p.items.includes(itemIdx.value)) {
    // Update locally so the UI reflects the association immediately
    p.items.push(itemIdx.value);
    // Persist the association via patch so the backend knows this item belongs to the person
    emit('patch', { action: `person.${selectedPersonIdx.value}.items`, value: p.items, certificated_diamond: true });
  }
};

// Emit patch on change as specified: action "person.<person_id>.name" with the name value
const commitName = () => {
  const personId = selectedPersonIdx.value;
  let value = (nameModel.value || "").trim();
  if (!value) {
    // If name is empty, store default name: "Item <person_id>"
    value = t(pageStr + '.person_n', {n: personId + 1});
    // Reflect the default in the UI immediately
    nameModel.value = value;
  }
  emit('patch', { action: `person.${personId}.name`, value, certificated_diamond: true });
};

// Controls expand/collapse like itemV2VariantsList "closed" view
const showValues = ref(false);

// Determine whether current input is a new name not present anywhere in the session
const currentPersonName = computed(() => persons.value?.[selectedPersonIdx.value]?.name ?? "");
const normalizeName = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();
const sessionNamesSet = computed(() => new Set((persons.value || []).map((p: any) => normalizeName(p?.name))));
const isDirty = computed(() => normalizeName(nameModel.value) !== normalizeName(currentPersonName.value));
const nameInSession = computed(() => sessionNamesSet.value.has(normalizeName(nameModel.value)));
const showSaveIcon = computed(() => isDirty.value && !nameInSession.value);
</script>

<template>
  <div class="block w-screen px-4 md:w-full text-sm">
    <!-- Header mimicking itemV2VariantsList closed state -->
    <div class="flex items-center w-full">
      <div class="flex-grow flex items-center">
        <span @click="showValues = !showValues" class="cursor-pointer">
          <span class="text-black font-bold pr-1 text-xs">{{ t(pageStr + '.article_for') }}</span>
          <span class="font-light text-xs">{{ nameModel }}</span>
        </span>
      </div>
      <div class="flex-shrink-0 w-6 cursor-pointer text-brand-main" @click="showValues = !showValues">
        <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-4 h-4 transition-transform duration-300"
            :class="{ 'rotate-180': showValues }"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>

    <!-- Expanded content -->
    <div v-if="showValues" class="space-y-4 my-2">
      <!-- Person selector (shown when multiple persons exist) -->
      <div class="w-full" v-if="persons.length > 1">
        <select
            v-model.number="selectedPersonIdx"
            class="text-sm block w-full bg-white p-2 border border-brand-main">
          <option v-for="(p, idx) in persons" :key="'p'+idx" :value="idx">{{ t(pageStr + '.person_idx_name', { idx: idx + 1, name: p.name }) }}</option>
        </select>
      </div>

      <div class="relative">
        <input
            type="text"
            v-model="nameModel"
            @change="commitName"
            class="text-base block w-full bg-white p-2 pr-12 border border-brand-main"
        />
        <!-- Save icon button on the right of the input -->
        <button
            type="button"
            class="absolute inset-y-0 right-2 my-auto h-8 w-8 flex items-center justify-center text-brand-main hover:text-white hover:bg-brand-main rounded"
            @click="commitName"
            :title="showSaveIcon ? t(pageStr + '.save_new_name') : t(pageStr + '.saved')"
        >
          <!-- Save icon when name is changed to something not in session -->
          <svg v-if="showSaveIcon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5">
            <!-- Arrow Down Tray icon -->
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 10.5L12 15l4.5-4.5M12 15V3" />
          </svg>
          <!-- Check icon (current value exists in session or unchanged) -->
          <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>